/**
 * Club Service
 * Handles business logic for clubs and communities
 */

import clubsRepository, { Club } from '../repositories/clubs.repository'
// Import the runtime Vuex store instance, not the module definition
// @ts-ignore - Ignore missing type definitions for store.js
import store from '@/store.js'
import ClubRepository from '@/repositories/club.js'
import { CreateCommunityDTO, InviteMemberDTO, AssignOwnerDTO, AssignOwnerFormData } from '../types'

class ClubService {
  /**
   * Get the main club/community for the current partner
   */
  async getPartnerMainClub(): Promise<Club | null> {
    try {
      // Access the live Vuex store state through getters
      const partnerId = store.getters['broker/partnerId']
      console.log('Fetching main club for partnerId:', partnerId);
      if (!partnerId) {
        throw new Error('No partner ID available')
      }

      const mainClub = await clubsRepository.getPartnerMainCommunity(partnerId)
      let clubMembers;
      try {
        clubMembers = mainClub ? await ClubRepository.getAllClubMembers(mainClub.club.id) : []
      } catch (error) {
        console.error('Error fetching club members:', error)
        clubMembers = []
      }
      store.dispatch('setClubMembers', clubMembers)
      store.dispatch('broker/setMainClubId', mainClub ? mainClub.club.id : null)
      return mainClub
    } catch (error) {
      console.error('Failed to fetch partner main club:', error)
      return null
    }
  }

  /**
   * Get all clubs for the current partner
   */
  async getAllPartnerClubs(): Promise<Club[]> {
    try {
      // Access the live Vuex store state through getters
      const partnerId = store.getters['broker/partnerId']
      if (!partnerId) {
        throw new Error('No partner ID available')
      }

      return await clubsRepository.getPartnerClubs(partnerId)
    } catch (error) {
      console.error('Failed to fetch partner clubs:', error)
      return []
    }
  }

  /**
   * Format club subscription expiration date
   */
  formatExpirationDate(expirationTime: string): string {
    const date = new Date(expirationTime)
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }
    return date.toLocaleDateString('en-US', options)
  }

  /**
   * Calculate days until club subscription expires
   */
  getDaysUntilExpiration(expirationTime: string): number {
    const now = new Date()
    const expiration = new Date(expirationTime)
    const difference = expiration.getTime() - now.getTime()
    return Math.ceil(difference / (1000 * 60 * 60 * 24))
  }

  /**
   * Check if a club subscription is active
   */
  isSubscriptionActive(expirationTime: string): boolean {
    const now = new Date()
    const expiration = new Date(expirationTime)
    return expiration > now
  }

  /**
   * Get subscription status text
   */
  getSubscriptionStatus(club: Club): {
    status: 'active' | 'expired' | 'expiring_soon'
    text: string
  } {
    const daysUntilExpiration = this.getDaysUntilExpiration(club.proExpirationTime)

    if (daysUntilExpiration <= 0) {
      return {
        status: 'expired',
        text: 'Subscription expired'
      }
    } else if (daysUntilExpiration <= 7) {
      return {
        status: 'expiring_soon',
        text: `Expires in ${daysUntilExpiration} days`
      }
    } else {
      return {
        status: 'active',
        text: `Active until ${this.formatExpirationDate(club.proExpirationTime)}`
      }
    }
  }

  /**
   * Create a new community
   */
  async createCommunity(communityData: CreateCommunityDTO): Promise<Club> {
    try {
      // Ensure userId is included from the store
      const userId = store.state.broker?.user?.id
      if (!userId || userId === 0) {
        throw new Error('User ID not available. Please log in again.')
      }

      console.log('Creating community with partnerOwned:', communityData.partnerOwned)

      const clubDataWithUserId = {
        ...communityData,
        // partnerOwned is now passed from the wizard based on user's choice
        // No need to override it - keep the value from communityData
        userId: String(userId) // Convert numeric ID to string for API
      }

      console.log('Creating community with user ID:', userId)
      const club = await clubsRepository.createClub(clubDataWithUserId)

      // Refresh main club after creation if this is a partner-owned club
      if (communityData.partnerOwned) {
        await this.getPartnerMainClub()
      }

      return club
    } catch (error) {
      console.error('Error creating community:', error)
      throw error
    }
  }

  /**
   * Invite members to an existing club/community
   * @param clubId - The club/community ID
   * @param inviteData - Object containing emails (CSV files are processed client-side)
   */
  async inviteMembers(clubId: number, inviteData: { emails: string[] }): Promise<number> {
    try {
      // Check if we have emails to send
      if (!inviteData.emails || inviteData.emails.length === 0) {
        throw new Error('No emails provided')
      }

      const invitationData: InviteMemberDTO = {
        emails: inviteData.emails,
        clubId
      }

      console.log('Sending invitations with data:', invitationData)
      const sentCount = await clubsRepository.inviteMembers(invitationData)
      return sentCount
    } catch (error) {
      console.error('Error inviting members:', error)
      throw error
    }
  }

  /**
   * Get social invite code for a club
   * @param clubId - The club/community ID
   * @returns Promise with the invite code string
   */
  async getSocialInviteCode(clubId: number): Promise<string> {
    try {
      const inviteCode = await clubsRepository.getSocialInviteCode(clubId)
      return inviteCode
    } catch (error) {
      console.error('Error getting social invite code:', error)
      throw error
    }
  }

  /**
   * Send admin invitation to manage a community
   * Transforms form data into API payload and sends the invitation
   * @param formData - The form data from AssignOwnerModal (name, email, phone, membersLimit, notifyOnAccept)
   * @returns Promise that resolves when invitation is sent
   */
  async sendAdminInvitation(formData: AssignOwnerFormData): Promise<void> {
    try {
      // Get inviterId (user.id) and partnerId from store
      const userId = store.state.broker?.user?.id
      const partnerId = store.getters['broker/partnerId']

      if (!userId) {
        throw new Error('User ID not available. Please log in again.')
      }

      if (!partnerId) {
        throw new Error('Partner ID not available. Please log in again.')
      }

      // Transform form data to API payload
      const apiPayload: AssignOwnerDTO = {
        emails: [formData.email.trim()],
        threshold: formData.membersLimit,
        notifyPartner: formData.notifyOnAccept,
        inviterId: userId,
        partnerId: partnerId
      }

      console.log('Sending admin invitation with payload:', apiPayload)
      await clubsRepository.sendAdminInvitation(apiPayload)
    } catch (error) {
      console.error('Error sending admin invitation:', error)
      throw error
    }
  }
}

export default new ClubService()